import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

import { FormattedProps, FormattedUpdatingOptionsSummaryData } from '../types';
import {
  ErrorContainer,
  ButtonsWrapper,
  HeadContainer,
  Option,
  OptionsList,
  OptionsWrapper,
  Subtitle,
  Total,
  TotalLabel,
  Wrapper
} from '../styles/components';
import { CustomTitle } from 'shared/components/presentational/Title/sharedTitles';
import { useUpdatingOptionsSummary } from '../hooks/useUpdatingOptionsSummary';
import Loader from 'shared/blocks/selfcare/shared/components/Loader';
import OfferDetailsRocContainer from '../../../shared/containers/OfferDetailsRocContainer';
import AlertBox from 'shared/components/presentational/AlertBox/AlertBox';
import { Button } from '../../../shared/components/Button/Button';
import { formatUpdatingOptionsSummaryData } from '../helpers/formatUpdatingOptionsSummaryData';
import { SAVE_PAYMENT_DATA, UpdatingOptionsState } from '../../reducer';

function Summary({
  title,
  subtitle,
  totalLabel,
  productEngagementUnit,
  submitButton,
  previousButtonLabel,
  redirectLinkToStart,
  apiUpdatingOptionsSummary
}: FormattedProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const [
    summaryData,
    setSummaryData
  ] = useState<FormattedUpdatingOptionsSummaryData | null>(null);

  const updatingOptionsValues = useSelector(
    (state: { updatingOptions: UpdatingOptionsState }) =>
      state.updatingOptions || {}
  );
  const dispatch = useDispatch();

  const { isIdle, isError, isLoading, mutate } = useUpdatingOptionsSummary({
    apiUpdatingOptionsSummary,
    saleDeviceId: updatingOptionsValues.saleDeviceId
  });

  useEffect(() => {
    if (
      updatingOptionsValues.headers.token &&
      updatingOptionsValues.headers.correlationId &&
      !summaryData
    ) {
      const queryParams = new URLSearchParams(location.search);
      const majorProductId = queryParams.get('product');
      const minorProductsParam = queryParams.get('minorProducts');

      const productsArray = [
        majorProductId,
        ...(minorProductsParam ? minorProductsParam.split(',') : [])
      ].filter(Boolean);

      mutate(
        {
          token: updatingOptionsValues.headers.token,
          body: {
            equipmentId: updatingOptionsValues.renewalNumber,
            paymentMeanId: 'MP',
            data: { products: productsArray.map(id => ({ id })) }
          }
        },
        {
          onSuccess: data => {
            setSummaryData(formatUpdatingOptionsSummaryData(data));
          }
        }
      );
    }
  }, [updatingOptionsValues, location.search, mutate, summaryData]);

  if (
    isLoading ||
    (isIdle &&
      updatingOptionsValues.headers.token &&
      updatingOptionsValues.headers.correlationId)
  ) {
    return <Loader />;
  }

  if (isError || !summaryData) {
    return (
      <ErrorContainer>
        <AlertBox
          status="error"
          content={<Trans i18nKey="blocks.updatingOptions.error.description" />}
          link={{
            label: t('blocks.updatingOptions.error.linkLabel'),
            href: redirectLinkToStart
          }}
        />
      </ErrorContainer>
    );
  }

  const handleSubmit = () => {
    dispatch({
      type: SAVE_PAYMENT_DATA,
      payload: {
        amount: summaryData.amount,
        currency: summaryData.currency,
        tax: summaryData.tax
      }
    });
    history.push(submitButton.link);
  };

  const handleCancelButton = () => {
    history.goBack();
  };

  if (!summaryData.allowModart) {
    return (
      <ErrorContainer>
        <AlertBox
          status="error"
          title={t('blocks.updatingOptions.summary.error.unauthorized.title')}
          content={t(
            'blocks.updatingOptions.summary.error.unauthorized.content'
          )}
        />
        {previousButtonLabel && (
          <Button
            label={previousButtonLabel}
            variant="primary"
            fullWidthMobile
            fullWidthDesktop
            onClick={handleCancelButton}
          />
        )}
      </ErrorContainer>
    );
  }

  return (
    <Wrapper>
      <HeadContainer>
        {title && (
          <CustomTitle htmlTag={title.htmlTag}>{title.label}</CustomTitle>
        )}
        {subtitle && <Subtitle html={subtitle} />}
      </HeadContainer>

      <OptionsWrapper>
        <OptionsList>
          {summaryData.minorProducts.map(minorProduct => (
            <Option key={minorProduct.id}>{minorProduct.title}</Option>
          ))}
        </OptionsList>
        <Total>
          <TotalLabel>{totalLabel}</TotalLabel>
          <OfferDetailsRocContainer
            price={summaryData.amount}
            variant="modal"
            currency={summaryData.currency}
            engagementUnit={productEngagementUnit}
          />
        </Total>
      </OptionsWrapper>

      <ButtonsWrapper>
        <Button
          label={submitButton.label}
          variant="primary"
          fullWidthMobile
          fullWidthDesktop
          onClick={handleSubmit}
        />
        {previousButtonLabel && (
          <Button
            label={previousButtonLabel}
            variant="secondary"
            fullWidthMobile
            fullWidthDesktop
            onClick={handleCancelButton}
          />
        )}
      </ButtonsWrapper>
    </Wrapper>
  );
}

export default Summary;
